import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView,
  trackEvent,
  trackUserEngagement,
  trackTradingEvent,
  trackRuleEvent,
  trackError,
} from '../utils/analytics';

export const useAnalytics = () => {
  const location = useLocation();

  // Track page view when location changes
  const trackCurrentPage = useCallback(() => {
    const pageTitle = document.title || 'MarketEye - Trade Desk';
    trackPageView(pageTitle, location.pathname);
  }, [location.pathname]);

  // Track button clicks and user interactions
  const trackButtonClick = useCallback((buttonName: string, page?: string) => {
    trackEvent('button_click', 'user_interaction', buttonName);
    trackUserEngagement('button_click', {
      button_name: buttonName,
      page: page || location.pathname,
    });
  }, [location.pathname]);

  // Track form submissions
  const trackFormSubmission = useCallback((formName: string, success: boolean, errorMessage?: string) => {
    trackEvent('form_submit', 'user_interaction', formName, success ? 1 : 0);
    trackUserEngagement('form_submit', {
      form_name: formName,
      success,
      error_message: errorMessage,
    });
  }, []);

  // Track file uploads
  const trackFileUpload = useCallback((fileType: string, fileName: string, success: boolean, errorMessage?: string) => {
    trackEvent('file_upload', 'user_interaction', fileName, success ? 1 : 0);
    trackUserEngagement('file_upload', {
      file_type: fileType,
      file_name: fileName,
      success,
      error_message: errorMessage,
    });
  }, []);

  // Track trading actions
  const trackTradingAction = useCallback((action: string, symbol?: string, quantity?: number, price?: number) => {
    trackTradingEvent(action, symbol, quantity, price);
  }, []);

  // Track rule actions
  const trackRuleAction = useCallback((action: string, ruleName?: string, ruleType?: string, triggered?: boolean) => {
    trackRuleEvent(action, ruleName, ruleType, triggered);
  }, []);

  // Track errors
  const trackAppError = useCallback((errorType: string, errorMessage: string, errorStack?: string) => {
    trackError(errorType, errorMessage, errorStack);
  }, []);

  // Track navigation
  const trackNavigation = useCallback((from: string, to: string) => {
    trackEvent('navigation', 'user_interaction', `${from} -> ${to}`);
    trackUserEngagement('navigation', {
      from_page: from,
      to_page: to,
    });
  }, []);

  // Track settings changes
  const trackSettingsChange = useCallback((setting: string, value: unknown) => {
    trackEvent('settings_change', 'user_interaction', setting);
    trackUserEngagement('settings_change', {
      setting,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    });
  }, []);

  // Track API calls
  const trackAPICall = useCallback((endpoint: string, method: string, success: boolean, duration?: number) => {
    trackEvent('api_call', 'system', endpoint, success ? 1 : 0);
    trackUserEngagement('api_call', {
      endpoint,
      method,
      success,
      duration,
    });
  }, []);

  // Track alert events
  const trackAlertEvent = useCallback((action: string, alertType?: string, symbol?: string, triggered?: boolean) => {
    trackEvent('alert_action', 'alerts', action);
    trackUserEngagement('alert_action', {
      action,
      alert_type: alertType,
      symbol,
      triggered,
    });
  }, []);

  // Track email events
  const trackEmailEvent = useCallback((action: string, emailType?: string, success?: boolean) => {
    trackEvent('email_action', 'notifications', action);
    trackUserEngagement('email_action', {
      action,
      email_type: emailType,
      success,
    });
  }, []);

  // Track resend events
  const trackResendEvent = useCallback((action: string, endpointType?: string, success?: boolean) => {
    trackEvent('resend_action', 'notifications', action);
    trackUserEngagement('resend_action', {
      action,
      endpoint_type: endpointType,
      success,
    });
  }, []);

  return {
    trackCurrentPage,
    trackButtonClick,
    trackFormSubmission,
    trackFileUpload,
    trackTradingAction,
    trackRuleAction,
    trackAppError,
    trackNavigation,
    trackSettingsChange,
    trackAPICall,
    trackAlertEvent,
    trackEmailEvent,
    trackResendEvent,
  };
};
